import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  TextField,
  InlineStack,
  Text,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
  Checkbox,
} from '@shopify/polaris';
import { clinicsApi } from '@/services';
import type { Clinic } from '@/types';

export default function ClinicsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const emptyClinic = {
    name: '',
    email: '',
    phone: '',
    billingTerms: 'net_30',
    taxExempt: false,
    isConsignmentClinic: false,
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    billingSameAsShipping: true,
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingProvince: '',
    billingZip: '',
  };
  const [newClinic, setNewClinic] = useState({ ...emptyClinic });
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['clinics', page, status, searchQuery],
    queryFn: () =>
      clinicsApi.list({
        page,
        limit,
        status: status[0],
        search: searchQuery || undefined,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof newClinic) => {
      const shippingAddress = data.address1
        ? {
            address1: data.address1,
            address2: data.address2 || undefined,
            city: data.city,
            province: data.province,
            zip: data.zip,
            countryCode: 'CA',
          }
        : undefined;
      const billingAddress = data.billingSameAsShipping
        ? shippingAddress
        : data.billingAddress1
          ? {
              address1: data.billingAddress1,
              address2: data.billingAddress2 || undefined,
              city: data.billingCity,
              province: data.billingProvince,
              zip: data.billingZip,
              countryCode: 'CA',
            }
          : undefined;
      return clinicsApi.create({
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        billingTerms: data.billingTerms,
        taxExempt: data.taxExempt,
        isConsignmentClinic: data.isConsignmentClinic,
        shippingAddress,
        billingAddress,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      setShowCreateModal(false);
      setNewClinic({ ...emptyClinic });
    },
  });

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setSearchQuery('');
  }, []);

  const handleCreateClinic = useCallback(() => {
    if (!newClinic.name) return;
    createMutation.mutate(newClinic);
  }, [newClinic, createMutation]);

  const getStatusBadge = (clinicStatus: string) => {
    switch (clinicStatus) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'inactive':
        return <Badge>Inactive</Badge>;
      case 'suspended':
        return <Badge tone="critical">Suspended</Badge>;
      default:
        return <Badge>{clinicStatus}</Badge>;
    }
  };

  const rows = (data?.data || []).map((clinic: Clinic & { _count?: { rentals: number }; primaryAgent?: string }) => [
    <Button key={clinic.id} variant="plain" onClick={() => navigate(`/clinics/${clinic.id}`)}>
      {clinic.name}
    </Button>,
    clinic.email || 'N/A',
    clinic.phone || 'N/A',
    clinic.billingTerms,
    clinic.taxExempt ? 'Yes' : 'No',
    getStatusBadge(clinic.status),
    <Button key={`view-${clinic.id}`} onClick={() => navigate(`/clinics/${clinic.id}`)}>
      View
    </Button>,
  ]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
            { label: 'Suspended', value: 'suspended' },
          ]}
          selected={status}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = status.length > 0
    ? [{ key: 'status', label: `Status: ${status.join(', ')}`, onRemove: () => setStatus([]) }]
    : [];

  if (error) {
    return (
      <Page title="Clinics">
        <Card>
          <EmptyState
            heading="Error loading clinics"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Clinics"
      primaryAction={{
        content: 'Add Clinic',
        onAction: () => setShowCreateModal(true),
      }}
    >
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search clinics..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleSearchChange}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={handleClearFilters}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No clinics found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              action={{ content: 'Add Clinic', onAction: () => setShowCreateModal(true) }}
            >
              <p>Add your first clinic to get started.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Name', 'Email', 'Phone', 'Billing', 'Tax Exempt', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} clinics
          </Text>
        </Box>
      )}

      {/* Create Clinic Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Clinic"
        primaryAction={{
          content: 'Add Clinic',
          onAction: handleCreateClinic,
          disabled: !newClinic.name,
          loading: createMutation.isPending,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowCreateModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Clinic Name"
              value={newClinic.name}
              onChange={(value) => setNewClinic((prev) => ({ ...prev, name: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Email"
              type="email"
              value={newClinic.email}
              onChange={(value) => setNewClinic((prev) => ({ ...prev, email: value }))}
              autoComplete="off"
            />
            <TextField
              label="Phone"
              type="tel"
              value={newClinic.phone}
              onChange={(value) => setNewClinic((prev) => ({ ...prev, phone: value }))}
              autoComplete="off"
            />
            <Checkbox
              label="Consignment Clinic"
              helpText="This clinic keeps HealFaster equipment on-site and self-fulfills orders. A replenishment shipment will be auto-created for each clinic-fulfilled order."
              checked={newClinic.isConsignmentClinic}
              onChange={(value) => setNewClinic((prev) => ({ ...prev, isConsignmentClinic: value }))}
            />
            <Text as="h3" variant="headingSm">Shipping Address</Text>
            <TextField
              label="Street Address"
              value={newClinic.address1}
              onChange={(value) => setNewClinic((prev) => ({ ...prev, address1: value }))}
              autoComplete="off"
            />
            <TextField
              label="Suite / Unit (optional)"
              value={newClinic.address2}
              onChange={(value) => setNewClinic((prev) => ({ ...prev, address2: value }))}
              autoComplete="off"
            />
            <InlineStack gap="400">
              <Box width="40%">
                <TextField
                  label="City"
                  value={newClinic.city}
                  onChange={(value) => setNewClinic((prev) => ({ ...prev, city: value }))}
                  autoComplete="off"
                />
              </Box>
              <Box width="25%">
                <TextField
                  label="Province"
                  value={newClinic.province}
                  onChange={(value) => setNewClinic((prev) => ({ ...prev, province: value }))}
                  placeholder="ON"
                  autoComplete="off"
                />
              </Box>
              <Box width="25%">
                <TextField
                  label="Postal Code"
                  value={newClinic.zip}
                  onChange={(value) => setNewClinic((prev) => ({ ...prev, zip: value }))}
                  autoComplete="off"
                />
              </Box>
            </InlineStack>
            <Checkbox
              label="Billing address same as shipping"
              checked={newClinic.billingSameAsShipping}
              onChange={(value) => setNewClinic((prev) => ({ ...prev, billingSameAsShipping: value }))}
            />
            {!newClinic.billingSameAsShipping && (
              <>
                <Text as="h3" variant="headingSm">Billing Address</Text>
                <TextField
                  label="Street Address"
                  value={newClinic.billingAddress1}
                  onChange={(value) => setNewClinic((prev) => ({ ...prev, billingAddress1: value }))}
                  autoComplete="off"
                />
                <TextField
                  label="Suite / Unit (optional)"
                  value={newClinic.billingAddress2}
                  onChange={(value) => setNewClinic((prev) => ({ ...prev, billingAddress2: value }))}
                  autoComplete="off"
                />
                <InlineStack gap="400">
                  <Box width="40%">
                    <TextField
                      label="City"
                      value={newClinic.billingCity}
                      onChange={(value) => setNewClinic((prev) => ({ ...prev, billingCity: value }))}
                      autoComplete="off"
                    />
                  </Box>
                  <Box width="25%">
                    <TextField
                      label="Province"
                      value={newClinic.billingProvince}
                      onChange={(value) => setNewClinic((prev) => ({ ...prev, billingProvince: value }))}
                      placeholder="ON"
                      autoComplete="off"
                    />
                  </Box>
                  <Box width="25%">
                    <TextField
                      label="Postal Code"
                      value={newClinic.billingZip}
                      onChange={(value) => setNewClinic((prev) => ({ ...prev, billingZip: value }))}
                      autoComplete="off"
                    />
                  </Box>
                </InlineStack>
              </>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
